import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Trash2, FileText, ExternalLink, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";
import { fmtDateTime } from "@/lib/dates";

const RANGE_OPTIONS = [
  { value: 1, label: "Today" },
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

/**
 * Recently Deleted — an Admin/Manager audit feed of task attachment removals.
 *
 * Pulls from /api/recently-deleted (audit-log driven). Lets the user:
 *  • filter by time window (1 / 7 / 30 / 90 days)
 *  • open the originating task to re-upload or restore from elsewhere
 */
export default function RecentlyDeleted() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState({ rows: [], days: 7, count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/recently-deleted", { params: { days } });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(parseApiError(e, "Failed to load"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  return (
    <div className="max-w-5xl mx-auto" data-testid="recently-deleted-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#0a2540]" style={{ fontFamily: "Outfit" }}>Recently Deleted</h1>
          <p className="text-xs text-slate-500 mt-1">Audit feed of task attachment removals — files and drive links. Use this to re-upload from a backup or spot accidental deletes.</p>
        </div>
        <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden" data-testid="rd-range">
          {RANGE_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => setDays(o.value)}
              className={`px-3 py-1.5 text-xs ${days === o.value ? "bg-[#0a2540] text-white" : "bg-white text-slate-600 hover:text-[#0a2540]"}`}
              data-testid={`rd-range-${o.value}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <Card className="border-slate-200 shadow-none rounded-sm">
        <div className="px-4 py-2.5 border-b border-slate-200 text-[11px] font-mono-data text-slate-500" data-testid="rd-status">
          {loading
            ? "Loading…"
            : `${data.count} deletion${data.count === 1 ? "" : "s"} in the last ${data.days} day${data.days === 1 ? "" : "s"}`}
        </div>
        {data.rows.length === 0 && !loading ? (
          <div className="px-4 py-12 text-center text-xs text-slate-400" data-testid="rd-empty">
            Nothing has been deleted in this window. 🎉
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="rd-list">
            {data.rows.map((row, i) => {
              const Icon = row.kind === "file" ? FileText : ExternalLink;
              return (
                <li
                  key={`${row.kind}-${row.deleted_at}-${i}`}
                  className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50"
                  data-testid={`rd-row-${i}`}
                >
                  <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-sm border border-red-200 bg-red-50 text-red-600">
                    <Trash2 size={12} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 font-mono-data text-[10px] uppercase tracking-wider" data-testid={`rd-kind-${i}`}>
                        {row.kind}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[#0a2540] font-medium truncate max-w-[280px] sm:max-w-[420px]">
                        <Icon size={11} className="shrink-0" />
                        {row.kind === "link" && row.label?.startsWith("http") ? (
                          <a href={row.label} target="_blank" rel="noreferrer" className="hover:underline truncate" title={row.label}>{row.label}</a>
                        ) : (
                          <span className="truncate" title={row.label}>{row.label}</span>
                        )}
                      </span>
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500 truncate">
                      Deleted by <span className="text-slate-700">{row.deleted_by_name || "—"}</span>{" "}
                      <span className="text-slate-300">·</span>{" "}
                      <span className="font-mono-data">{fmtDateTime(row.deleted_at)}</span>
                      {row.task_title && (
                        <>
                          {" "}<span className="text-slate-300">·</span>{" "}
                          from task <span className="text-slate-700">“{row.task_title}”</span>
                        </>
                      )}
                    </div>
                  </div>
                  {row.project_id && (
                    <Link
                      to={`/projects/${row.project_id}`}
                      className="shrink-0 text-[11px] text-slate-500 hover:text-[#0a2540] inline-flex items-center gap-1"
                      data-testid={`rd-open-${i}`}
                    >
                      Open task <ArrowRight size={11} />
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
